/**
 * 炼金道具图数据模型。
 *
 * 本文件只定义道具逻辑图的运行期数据结构、节点类型、端口契约和默认工厂函数。
 * `graph` 内的数据需要能被游戏运行器直接解释；画布坐标、节点标题、备注等编辑器表现数据统一放在
 * `editor` 字段中，运行器可以完全忽略。
 *
 * 关键设计：
 * 1. 节点以 `Record<nodeId, node>` 保存，运行器可按 id O(1) 查找节点。
 * 2. `flows` 只表示流程推进关系；`bindings` 只表示值、目标、条件等输入依赖。
 * 3. `entryNodeIds` 显式记录事件入口，运行器不需要遍历所有节点寻找起点。
 * 4. 端口由节点类型静态定义，节点实例只保存业务参数，避免运行数据混入编辑器文案。
 */

/**
 * 节点类型枚举。
 *
 * 每个字符串都代表一种固定的运行语义，创建后不应在节点实例上动态修改。
 * 新增节点类型时，需要同步补充节点数据类型、端口定义、默认节点创建逻辑和默认标题。
 */
export type PropNodeType =
  | "eventTurnStart"
  | "eventAcquire"
  | "eventUse"
  | "eventEliminated"
  | "targetSelf"
  | "targetBoard"
  | "prototypeLibrary"
  | "prototypeSelect"
  | "targetFilter"
  | "filterRarity"
  | "filterTag"
  | "filterPrototype"
  | "filterRandom"
  | "actionGrow"
  | "actionExtraYield"
  | "actionPurify"
  | "actionProtect"
  | "actionAccelerate"
  | "actionTransform"
  | "actionSpawn"
  | "actionEliminate"
  | "actionCollect"
  | "actionHarvest"
  | "actionRarityWeight"
  | "actionExtendCommission"
  | "actionSwapPosition"
  | "actionPermanentBoost"
  | "actionTriggerVisual"
  | "intConstant"
  | "intAttribute"
  | "intQuantity"
  | "intKindCount"
  | "intRandom"
  | "intOperation"
  | "floatConstant"
  | "floatAttribute"
  | "floatRandom"
  | "floatOperation"
  | "roundNumber"
  | "rarityConstant"
  | "tagConstant"
  | "logicCompareInt"
  | "logicCompareFloat"
  | "logicContains"
  | "logicProbability";

/**
 * 端口类别。
 *
 * `flow` 用于控制执行顺序，只能出现在 `flows` 中。
 * 其它类别用于输入绑定，负责把目标、数值、条件或配置常量传递给节点。
 */
export type PropGraphPortKind = "flow" | "value" | "target" | "condition" | "rarity" | "tag";

/** 可写入 `bindings` 的端口类别。非流程端口不会推动节点执行，只提供运行期输入。 */
export type PropGraphBindingKind = Exclude<PropGraphPortKind, "flow">;

/**
 * 端口承载的语义值类型。
 *
 * 该类型用于编辑器连线校验和运行器输入校验，不等同于 TypeScript 静态类型。
 * 例如同为 `target` 端口类别的端口，也会通过 `propArray` 进一步标明运行期传递的是道具数组。
 */
export type PropGraphValueType =
  | "flow"
  | "propArray"
  | "integer"
  | "float"
  | "boolean"
  | "probability"
  | "multiplier"
  | "rarity"
  | "tag";

/**
 * 节点端口定义。
 *
 * 端口定义只保存在静态配置中，不写入节点实例。运行器和编辑器通过节点类型读取端口契约，
 * 从而保证数据库中的节点只保存真正影响游戏逻辑的参数。
 */
export interface PropGraphPort {
  /** 稳定端口 id。连接和绑定通过该字段定位具体输入或输出，例如 `flow`、`targets`、`amount`。 */
  id: string;
  /** 编辑器显示名。运行器不应依赖该字段做逻辑判断。 */
  name: string;
  /** 端口类别，用于判断流程、值、目标、条件等通道是否允许连接。 */
  kind: PropGraphPortKind;
  /** 端口值类型，用于进一步校验整数、浮点数、布尔、道具数组等语义是否匹配。 */
  valueType?: PropGraphValueType;
  /** 是否允许一个输入端口承接多条同类绑定。未设置时默认只接受一条。 */
  multiple?: boolean;
  /** 是否为必填输入端口。未设置时按必填处理；输出端口忽略该字段。 */
  required?: boolean;
}

/** 画布中的二维坐标。仅用于编辑器布局，运行器不读取。 */
export interface PropGraphPosition {
  x: number;
  y: number;
}

/**
 * 单个节点的编辑器状态。
 *
 * 这些字段不参与游戏运行，但需要随图保存，以便编辑器重新打开时恢复节点布局和编辑状态。
 */
export interface PropGraphNodeEditorState {
  /** 对应的运行节点 id。 */
  nodeId: string;
  /** 节点在画布上的显示标题。 */
  title: string;
  /** 节点在画布上的位置。 */
  position: PropGraphPosition;
  /** 编辑期禁用标记。运行器或导出流程可据此跳过该节点。 */
  disabled?: boolean;
}

/** 编辑器备注。备注只用于策划说明，不参与运行，也不作为节点处理。 */
export interface PropGraphEditorComment {
  id: string;
  text: string;
  position: PropGraphPosition;
}

/**
 * 编辑器状态集合。
 *
 * `nodes` 用 nodeId 关联运行节点，`comments` 保存纯编辑器备注。
 */
export interface PropGraphEditorState {
  nodes: PropGraphNodeEditorState[];
  comments: PropGraphEditorComment[];
}

/**
 * 运行节点基础结构。
 *
 * `TType` 决定节点的端口定义和数据结构；`TData` 是运行器真正需要解释的参数。
 */
export interface BasePropNode<TType extends PropNodeType, TData> {
  /** 节点 id。流程、输入绑定和编辑器状态都通过该 id 关联节点。 */
  id: string;
  /** 节点类型。创建后固定，决定节点参数结构和端口定义。 */
  type: TType;
  /** 节点参数。只保存影响游戏逻辑的配置，不保存画布表现数据。 */
  data: TData;
}

/** 回合开始事件节点参数。 */
export interface EventTurnStartNodeData {
  /** 触发间隔。0 表示每轮触发；大于 0 表示间隔若干轮后触发。 */
  interval: number;
  /** 最大触发次数。未设置时表示不限制触发次数。 */
  loop?: number;
}

/** 持有触发事件节点参数。只要实例仍被持有，每轮都会触发；实例移除后停止触发。 */
export interface EventAcquireNodeData {}

/** 主动使用事件节点参数。被玩家主动使用本道具时触发，无额外参数。 */
export interface EventUseNodeData {}

/** 消除触发事件节点参数。监听目标来自 `target` 输入端口，无额外参数。 */
export interface EventEliminatedNodeData {}

/** 自身目标节点参数。输出本道具自身，无额外参数。 */
export interface TargetSelfNodeData {}

/** 盘面原料范围类型。 */
export type BoardMaterialScope = "all" | "relative" | "absolute";

/** 相对当前位置的盘面范围。 */
export type RelativePositionKind = "adjacent" | "sameRow" | "sameColumn" | "diagonal";

/** 盘面固定区域范围。 */
export type AbsolutePositionKind =
  | "corner"
  | "edge"
  | "firstRow"
  | "lastRow"
  | "firstColumn"
  | "lastColumn"
  | "middleColumn";

/** 盘面原料节点可使用的位置规则。 */
export type BoardMaterialPosition = RelativePositionKind | AbsolutePositionKind;

/**
 * 盘面原料节点参数。
 *
 * `scope` 为 `all` 时输出全部盘面原料；为 `relative` 或 `absolute` 时需要 `position`
 * 指定一个或多个位置规则。旧数据中的单个位置字符串仍然有效，编辑器保存时会规范为数组。
 */
export interface TargetBoardNodeData {
  scope: BoardMaterialScope;
  position?: BoardMaterialPosition | BoardMaterialPosition[];
  /** 是否将数据库“空位置”原型创建的盘面占位实例也作为目标输出。默认 false。 */
  includeEmpty?: boolean;
}

/** 原型库可输出的道具类型。1=原料，2=辅料，3=配方。 */
export type PrototypeLibraryPropType = 1 | 2 | 3;

/** 原型库节点参数。按道具类型输出可用原型，默认输出原料原型。 */
export interface PrototypeLibraryNodeData {
  /** 输出的道具类型。 */
  propType: PrototypeLibraryPropType;
}

/** 指定原型节点参数。从道具列表中选择一个固定道具作为目标原型。 */
export interface PrototypeSelectNodeData {
  /** 被选中的道具配置 id。未设置时表示尚未完成配置。 */
  prototypeId?: string;
}

/** 条件筛选节点参数。筛选逻辑完全由 `targets` 和 `condition` 输入端口提供。 */
export interface TargetFilterNodeData {}

/** 品质筛选节点参数。品质条件来自 `rarity` 输入端口。 */
export interface FilterRarityNodeData {}

/** 标签筛选节点参数。标签条件来自 `tag` 输入端口。 */
export interface FilterTagNodeData {}

/** 随机筛选节点参数。筛选数量来自 `count` 输入端口。 */
export interface FilterRandomNodeData {}

/** 动作节点通用参数。目标、数量、条件等运行输入仍然来自端口。 */
export interface ActionEffectNodeData {
  /** 是否将该动作标记为负面效果。默认 false，供运行器和净化逻辑识别。 */
  negative: boolean;
  /** 动作触发时，当前道具自身播放的特效配置 id 列表。为空表示无特效。 */
  selfEffects?: string[];
  /** 动作触发时，动作目标对象播放的特效配置 id 列表。为空表示无特效。 */
  targetEffects?: string[];
  /** 动作触发时，当前道具自身播放的动画配置 id 列表。为空表示无动画。 */
  selfAnimations?: string[];
  /** 动作触发时，动作目标对象播放的动画配置 id 列表。为空表示无动画。 */
  targetAnimations?: string[];
  /** @deprecated 旧版单选自身特效字段。编辑器读取时会归一化为 `selfEffects`，保存时不再写入。 */
  selfEffectId?: string;
  /** @deprecated 旧版单选目标特效字段。编辑器读取时会归一化为 `targetEffects`，保存时不再写入。 */
  targetEffectId?: string;
}

/** 永久加成动作参数。该动作会修改目标此后的产出，同标签加成按覆盖而不是叠加处理。 */
export interface ActionPermanentBoostNodeData extends ActionEffectNodeData {
  /** 互斥标签。运行期同一目标上相同标签的永久加成只保留最后一次效果，不同标签可以叠加。 */
  mutexTag: string;
}

/** 整数常量节点参数。 */
export interface IntConstantNodeData {
  /** 节点固定输出的整数值。 */
  value: number;
}

/** 浮点常量节点参数。 */
export interface FloatConstantNodeData {
  /** 节点固定输出的浮点值。 */
  value: number;
}

/** 品质常量节点参数。 */
export interface RarityConstantNodeData {
  /** 引用 proprarities 的品质值。 */
  value: number;
}

/** 标签常量节点参数。 */
export interface TagConstantNodeData {
  /** 引用 proptags 的标签值。 */
  value: number;
}

/** 可从目标读取的属性字段。产出会读取目标当前魔力产出，运行期需要包含永久加成。 */
export type TargetAttributeField = "rarity" | "tag" | "yield";

/** 目标属性取值节点参数。目标对象来自 `target` 输入端口。 */
export interface AttributeValueNodeData {
  /** 要从目标上读取的属性字段。 */
  attribute: TargetAttributeField;
}

/** 目标数量节点参数。目标数组来自 `targets` 输入端口，节点自身无额外参数。 */
export interface QuantityNodeData {}

/** 目标种类节点参数。统计目标数组中不同道具种类，节点自身无额外参数。 */
export interface KindCountNodeData {}

/** 随机取值节点参数。最小值和最大值都来自输入端口，节点自身不保存范围。 */
export interface RandomValueNodeData {}

/** 整数运算符。 */
export type IntegerOperationOperator =
  | "add"
  | "subtract"
  | "multiply"
  | "divide"
  | "mod"
  | "min"
  | "max";

/** 浮点运算符。 */
export type FloatOperationOperator =
  | "add"
  | "subtract"
  | "multiply"
  | "divide"
  | "mod"
  | "min"
  | "max";

/** 数值取整方式。 */
export type RoundMode = "round" | "ceil" | "floor";

/** 整数运算节点参数。左右值来自输入端口，节点内只保存运算符。 */
export interface IntegerOperationNodeData {
  /** 运算符。除法会向下取整；除零和取余零在运行期返回 0。 */
  op: IntegerOperationOperator;
}

/** 浮点运算节点参数。左右值来自输入端口，节点内只保存运算符。 */
export interface FloatOperationNodeData {
  /** 运算符。除零和取余零在运行期返回 0。 */
  op: FloatOperationOperator;
}

/** 取整节点参数。输入值来自 `value` 输入端口。 */
export interface RoundNumberNodeData {
  /** 取整方式。 */
  mode: RoundMode;
}

/** 比较运算符。用于整数比较和浮点比较节点。 */
export type CompareOperator = "eq" | "ne" | "gt" | "gte" | "lt" | "lte";

/** 逻辑比较节点参数。左右值来自输入端口，节点内只保存比较方式。 */
export interface CompareNodeData {
  /** 比较运算符。 */
  op: CompareOperator;
}

/** 概率条件节点参数。 */
export interface ProbabilityNodeData {
  /** 条件通过概率，使用 0-1 的浮点数表示。 */
  probability: number;
}

/** 事件节点类型别名。 */
export type EventTurnStartNode = BasePropNode<"eventTurnStart", EventTurnStartNodeData>;
export type EventAcquireNode = BasePropNode<"eventAcquire", EventAcquireNodeData>;
export type EventUseNode = BasePropNode<"eventUse", EventUseNodeData>;
export type EventEliminatedNode = BasePropNode<"eventEliminated", EventEliminatedNodeData>;
/** 目标节点类型别名。 */
export type TargetSelfNode = BasePropNode<"targetSelf", TargetSelfNodeData>;
export type TargetBoardNode = BasePropNode<"targetBoard", TargetBoardNodeData>;
/** 道具原型节点类型别名。 */
export type PrototypeLibraryNode = BasePropNode<"prototypeLibrary", PrototypeLibraryNodeData>;
export type PrototypeSelectNode = BasePropNode<"prototypeSelect", PrototypeSelectNodeData>;
/** 筛选节点类型别名。 */
export type TargetFilterNode = BasePropNode<"targetFilter", TargetFilterNodeData>;
export type FilterRarityNode = BasePropNode<"filterRarity", FilterRarityNodeData>;
export type FilterTagNode = BasePropNode<"filterTag", FilterTagNodeData>;
export type FilterPrototypeNode = BasePropNode<"filterPrototype", {}>;
export type FilterRandomNode = BasePropNode<"filterRandom", FilterRandomNodeData>;
/** 动作节点类型别名。 */
export type ActionGrowNode = BasePropNode<"actionGrow", ActionEffectNodeData>;
export type ActionExtraYieldNode = BasePropNode<"actionExtraYield", ActionEffectNodeData>;
export type ActionPurifyNode = BasePropNode<"actionPurify", ActionEffectNodeData>;
export type ActionProtectNode = BasePropNode<"actionProtect", ActionEffectNodeData>;
export type ActionAccelerateNode = BasePropNode<"actionAccelerate", ActionEffectNodeData>;
export type ActionTransformNode = BasePropNode<"actionTransform", ActionEffectNodeData>;
export type ActionSpawnNode = BasePropNode<"actionSpawn", ActionEffectNodeData>;
export type ActionEliminateNode = BasePropNode<"actionEliminate", ActionEffectNodeData>;
export type ActionCollectNode = BasePropNode<"actionCollect", ActionEffectNodeData>;
export type ActionHarvestNode = BasePropNode<"actionHarvest", ActionEffectNodeData>;
export type ActionRarityWeightNode = BasePropNode<"actionRarityWeight", ActionEffectNodeData>;
export type ActionExtendCommissionNode = BasePropNode<"actionExtendCommission", ActionEffectNodeData>;
export type ActionSwapPositionNode = BasePropNode<"actionSwapPosition", ActionEffectNodeData>;
export type ActionPermanentBoostNode = BasePropNode<"actionPermanentBoost", ActionPermanentBoostNodeData>;
export type ActionTriggerVisualNode = BasePropNode<"actionTriggerVisual", ActionEffectNodeData>;
/** 取值节点类型别名。 */
export type IntConstantNode = BasePropNode<"intConstant", IntConstantNodeData>;
export type IntAttributeNode = BasePropNode<"intAttribute", AttributeValueNodeData>;
export type IntQuantityNode = BasePropNode<"intQuantity", QuantityNodeData>;
export type IntKindCountNode = BasePropNode<"intKindCount", KindCountNodeData>;
export type IntRandomNode = BasePropNode<"intRandom", RandomValueNodeData>;
export type IntOperationNode = BasePropNode<"intOperation", IntegerOperationNodeData>;
export type FloatConstantNode = BasePropNode<"floatConstant", FloatConstantNodeData>;
export type FloatAttributeNode = BasePropNode<"floatAttribute", AttributeValueNodeData>;
export type FloatRandomNode = BasePropNode<"floatRandom", RandomValueNodeData>;
export type FloatOperationNode = BasePropNode<"floatOperation", FloatOperationNodeData>;
export type RoundNumberNode = BasePropNode<"roundNumber", RoundNumberNodeData>;
export type RarityConstantNode = BasePropNode<"rarityConstant", RarityConstantNodeData>;
export type TagConstantNode = BasePropNode<"tagConstant", TagConstantNodeData>;
/** 条件节点类型别名。 */
export type LogicCompareIntNode = BasePropNode<"logicCompareInt", CompareNodeData>;
export type LogicCompareFloatNode = BasePropNode<"logicCompareFloat", CompareNodeData>;
export type LogicContainsNode = BasePropNode<"logicContains", {}>;
export type LogicProbabilityNode = BasePropNode<"logicProbability", ProbabilityNodeData>;

/** 所有事件节点联合类型。 */
export type EventNode = EventTurnStartNode | EventAcquireNode | EventUseNode | EventEliminatedNode;
/** 所有目标节点联合类型。 */
export type TargetNode =
  | TargetSelfNode
  | TargetBoardNode;
/** 所有原型节点联合类型。 */
export type PrototypeNode =
  | PrototypeLibraryNode
  | PrototypeSelectNode;
/** 所有取值节点联合类型。 */
export type ValueNode =
  | IntConstantNode
  | IntAttributeNode
  | IntQuantityNode
  | IntKindCountNode
  | IntRandomNode
  | IntOperationNode
  | FloatConstantNode
  | FloatAttributeNode
  | FloatRandomNode
  | FloatOperationNode
  | RoundNumberNode
  | RarityConstantNode
  | TagConstantNode;
/** 所有动作节点联合类型。 */
export type ActionNode = ActionGrowNode | ActionExtraYieldNode | ActionPurifyNode | ActionProtectNode | ActionAccelerateNode | ActionTransformNode | ActionSpawnNode | ActionEliminateNode | ActionCollectNode | ActionHarvestNode | ActionRarityWeightNode | ActionExtendCommissionNode | ActionSwapPositionNode | ActionPermanentBoostNode | ActionTriggerVisualNode;
/** 所有筛选节点联合类型。 */
export type FilterNode =
  | TargetFilterNode
  | FilterRarityNode
  | FilterTagNode
  | FilterPrototypeNode
  | FilterRandomNode;
/** 所有条件节点联合类型。 */
export type LogicNode = LogicCompareIntNode | LogicCompareFloatNode | LogicContainsNode | LogicProbabilityNode;

/** 运行节点联合类型。运行器处理节点时通常以该类型作为入口。 */
export type PropNode =
  | EventNode
  | TargetNode
  | PrototypeNode
  | FilterNode
  | ValueNode
  | ActionNode
  | LogicNode;

/** 运行节点字典。key 必须等于节点自身的 id，以保证按 id 查找的结果稳定。 */
export type PropNodeMap = Record<string, PropNode>;

/** 运行连接端点。端点只引用节点 id 和固定端口 id，不包含任何显示字段。 */
export interface PropGraphEndpoint {
  /** 端点所属节点 id。 */
  nodeId: string;
  /** 节点端口 id。必须来自该节点类型的静态端口定义。 */
  portId: string;
}

/**
 * 流程连接。
 *
 * 运行器沿 `flows` 推进执行。`order` 用于同一个输出端口连接多个后继节点时保持稳定顺序。
 * 该结构只表达流程关系，不承载目标、数值或条件输入。
 */
export interface PropFlowConnection {
  /** 连接 id。便于编辑器选中、删除，也可用于运行日志定位。 */
  id: string;
  /** 起点输出端口，端口类别必须是 flow。 */
  from: PropGraphEndpoint;
  /** 终点输入端口，端口类别必须是 flow。 */
  to: PropGraphEndpoint;
  /** 同一输出端口下的执行顺序。 */
  order?: number;
}

/**
 * 输入绑定。
 *
 * 非 flow 连线不推动流程，只表示“某节点的某个输入端口从哪里取值、取目标或取条件”。
 * 游戏运行器求一个节点输入时，可以按 `target.nodeId + target.portId` 直接筛选这些绑定。
 */
export interface PropInputBinding {
  /** 绑定 id。便于编辑器选中、删除，也可用于运行日志定位。 */
  id: string;
  /** 绑定类别。必须与端口类别保持一致，且不能是 flow。 */
  kind: PropGraphBindingKind;
  /** 产生输入的输出端点。 */
  source: PropGraphEndpoint;
  /** 接收输入的输入端点。 */
  target: PropGraphEndpoint;
}

/**
 * 画布编辑器使用的统一连线视图。
 *
 * 数据库不直接存这个结构；保存时会根据 `kind` 写入 `flows` 或 `bindings`。
 * 这样编辑器可以用一套模型处理画布连线，而运行数据仍保持流程和输入分离。
 */
export interface PropEdge {
  /** 连线 id。 */
  id: string;
  /** 连线类别，决定保存时写入流程连接还是输入绑定。 */
  kind: PropGraphPortKind;
  /** 起点节点 id。 */
  fromNodeId: string;
  /** 起点端口 id。 */
  fromPortId: string;
  /** 终点节点 id。 */
  toNodeId: string;
  /** 终点端口 id。 */
  toPortId: string;
  /** 同一流程输出端口下的执行顺序。非流程连线通常不需要该字段。 */
  order?: number;
}

/**
 * 道具运行逻辑图。
 *
 * 该结构是游戏运行器解释道具行为的核心数据，不包含任何画布布局字段。
 */
export interface PropGraphDocument {
  /** 事件入口节点。运行器收到事件后优先从这些节点中匹配 event 参数。 */
  entryNodeIds: string[];
  /** 运行节点字典。 */
  nodes: PropNodeMap;
  /** 流程连接列表。只负责执行顺序，不提供运行输入。 */
  flows: PropFlowConnection[];
  /** 输入绑定列表。负责值、目标、条件、品质、标签等运行期输入。 */
  bindings: PropInputBinding[];
}

/**
 * 单个炼金道具的完整配置。
 *
 * 基础字段描述道具自身属性，`graph` 描述运行逻辑，`editor` 保存编辑器状态。
 */
export interface PropGraph {
  /** 数据库 id。 */
  _id: string;
  /** 道具名称。 */
  name: string;
  /** 美术资源编号。 */
  assetId: string;
  /** 道具类型，引用 proptypes。 */
  type: number;
  /** 道具品质，引用 proprarities。 */
  rarity: number;
  /** 道具标签，引用 proptags。 */
  tags: number[];
  /** 是否为衍生物。 */
  derived?: boolean;
  /** 基础产出。 */
  yield: number;
  /** 最后修改时间，毫秒时间戳，仅用于编辑器管理数据。 */
  updatedAt?: number;
  /** 最后修改人显示名，仅用于编辑器管理数据。 */
  updatedBy?: string;
  /** PropGraph 数据结构版本。当前固定为 3。 */
  schemaVersion: 3;
  /** 道具运行逻辑图。游戏逻辑只需要读取这一段和基础道具字段。 */
  graph: PropGraphDocument;
  /** 编辑器专用元数据。游戏运行器可以完整忽略。 */
  editor: PropGraphEditorState;
}

/** 每种节点类型的固定端口定义。 */
export type PropNodePortDefinition = {
  /** 输入端口列表。 */
  inputs: PropGraphPort[];
  /** 输出端口列表。 */
  outputs: PropGraphPort[];
};

/**
 * 节点端口定义表。
 *
 * 这是编辑器连线校验和运行器输入解析的单一事实来源。新增或修改节点端口时，应优先更新这里；
 * 节点实例中不应重复保存端口结构。
 */
export const PROP_NODE_PORTS: Record<
  PropNodeType,
  PropNodePortDefinition
> = {
  // 事件节点：接收游戏事件并输出流程信号。
  eventTurnStart: {
    inputs: [],
    outputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
    ],
  },
  eventAcquire: {
    inputs: [],
    outputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
    ],
  },
  eventUse: {
    inputs: [],
    outputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
    ],
  },
  eventEliminated: {
    inputs: [
      { id: "target", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
    ],
  },
  // 目标节点：从当前道具、盘面或原型配置中产生目标数组。
  targetSelf: {
    inputs: [],
    outputs: [
      { id: "target", name: "目标", kind: "target", valueType: "propArray" },
    ],
  },
  targetBoard: {
    inputs: [],
    outputs: [
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
    ],
  },
  prototypeLibrary: {
    inputs: [],
    outputs: [
      { id: "prototypes", name: "目标", kind: "target", valueType: "propArray" },
    ],
  },
  prototypeSelect: {
    inputs: [],
    outputs: [
      { id: "prototypes", name: "目标", kind: "target", valueType: "propArray" },
    ],
  },
  // 筛选节点：接收目标数组，并输出过滤后的目标数组。
  targetFilter: {
    inputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
      { id: "condition", name: "\u6761\u4ef6", kind: "condition", valueType: "boolean" },
    ],
    outputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
    ],
  },
  filterRarity: {
    inputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
      { id: "rarity", name: "\u54c1\u8d28", kind: "rarity", valueType: "rarity", multiple: true },
    ],
    outputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
    ],
  },
  filterTag: {
    inputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
      { id: "tag", name: "\u6807\u7b7e", kind: "tag", valueType: "tag", multiple: true },
    ],
    outputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
    ],
  },
  filterPrototype: {
    inputs: [
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "prototype", name: "原型", kind: "target", valueType: "propArray", multiple: true },
    ],
    outputs: [
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
    ],
  },
  filterRandom: {
    inputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
      { id: "count", name: "\u6570\u91cf", kind: "value", valueType: "integer" },
    ],
    outputs: [
      { id: "targets", name: "\u76ee\u6807", kind: "target", valueType: "propArray" },
    ],
  },
  // 动作节点：接收流程输入后修改目标状态，并通过多个流程出口表达执行结果。
  actionGrow: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionExtraYield: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer", required: false },
      { id: "multiplier", name: "倍率", kind: "value", valueType: "float", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionPermanentBoost: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer", required: false },
      { id: "multiplier", name: "倍率", kind: "value", valueType: "float", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionPurify: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionProtect: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionAccelerate: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionTransform: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "prototypes", name: "目标-转化", kind: "target", valueType: "propArray", multiple: true },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionSpawn: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "position", name: "位置", kind: "target", valueType: "propArray", required: false },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionEliminate: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionCollect: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "amount", name: "数量", kind: "value", valueType: "integer" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionHarvest: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionRarityWeight: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "rarity", name: "品质", kind: "rarity", valueType: "rarity" },
      { id: "multiplier", name: "倍率", kind: "value", valueType: "float" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionExtendCommission: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  actionSwapPosition: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
      { id: "positions", name: "位置", kind: "target", valueType: "propArray" },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  // 取值节点：把常量、目标属性或随机范围转换成运行期数值。
  actionTriggerVisual: {
    inputs: [
      { id: "flow", name: "流程", kind: "flow", valueType: "flow" },
      { id: "targets", name: "目标", kind: "target", valueType: "propArray", required: false },
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean", multiple: true, required: false },
    ],
    outputs: [
      { id: "complete", name: "流程-完成", kind: "flow", valueType: "flow" },
      { id: "fail", name: "流程-失败", kind: "flow", valueType: "flow" },
      { id: "each", name: "流程-每次", kind: "flow", valueType: "flow" },
    ],
  },
  intConstant: {
    inputs: [],
    outputs: [
      { id: "value", name: "整数", kind: "value", valueType: "integer" },
    ],
  },
  intAttribute: {
    inputs: [
      { id: "target", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "value", name: "整数", kind: "value", valueType: "integer" },
    ],
  },
  intQuantity: {
    inputs: [
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "value", name: "数量", kind: "value", valueType: "integer" },
    ],
  },
  intKindCount: {
    inputs: [
      { id: "targets", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "value", name: "数量", kind: "value", valueType: "integer" },
    ],
  },
  intRandom: {
    inputs: [
      { id: "min", name: "最小", kind: "value", valueType: "integer" },
      { id: "max", name: "最大", kind: "value", valueType: "integer" },
    ],
    outputs: [
      { id: "value", name: "结果", kind: "value", valueType: "integer" },
    ],
  },
  intOperation: {
    inputs: [
      { id: "left", name: "左值", kind: "value", valueType: "integer" },
      { id: "right", name: "右值", kind: "value", valueType: "integer" },
    ],
    outputs: [
      { id: "value", name: "结果", kind: "value", valueType: "integer" },
    ],
  },
  floatConstant: {
    inputs: [],
    outputs: [
      { id: "value", name: "浮点数", kind: "value", valueType: "float" },
    ],
  },
  floatAttribute: {
    inputs: [
      { id: "target", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "value", name: "浮点数", kind: "value", valueType: "float" },
    ],
  },
  floatRandom: {
    inputs: [
      { id: "min", name: "最小", kind: "value", valueType: "float" },
      { id: "max", name: "最大", kind: "value", valueType: "float" },
    ],
    outputs: [
      { id: "value", name: "结果", kind: "value", valueType: "float" },
    ],
  },
  floatOperation: {
    inputs: [
      { id: "left", name: "左值", kind: "value", valueType: "float" },
      { id: "right", name: "右值", kind: "value", valueType: "float" },
    ],
    outputs: [
      { id: "value", name: "结果", kind: "value", valueType: "float" },
    ],
  },
  roundNumber: {
    inputs: [
      { id: "value", name: "数值", kind: "value", valueType: "float" },
    ],
    outputs: [
      { id: "value", name: "整数", kind: "value", valueType: "integer" },
    ],
  },
  rarityConstant: {
    inputs: [],
    outputs: [
      { id: "value", name: "\u54c1\u8d28", kind: "rarity", valueType: "rarity" },
    ],
  },
  tagConstant: {
    inputs: [],
    outputs: [
      { id: "value", name: "\u6807\u7b7e", kind: "tag", valueType: "tag" },
    ],
  },
  // 条件节点：输出布尔条件，供筛选节点或动作节点的 condition 端口使用。
  logicCompareInt: {
    inputs: [
      { id: "left", name: "左值", kind: "value", valueType: "integer" },
      { id: "right", name: "右值", kind: "value", valueType: "integer" },
    ],
    outputs: [
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean" },
    ],
  },
  logicCompareFloat: {
    inputs: [
      { id: "left", name: "左值", kind: "value", valueType: "float" },
      { id: "right", name: "右值", kind: "value", valueType: "float" },
    ],
    outputs: [
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean" },
    ],
  },
  logicContains: {
    inputs: [
      { id: "targets", name: "目标-数组", kind: "target", valueType: "propArray" },
      { id: "target", name: "目标", kind: "target", valueType: "propArray" },
    ],
    outputs: [
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean" },
    ],
  },
  logicProbability: {
    inputs: [],
    outputs: [
      { id: "condition", name: "条件", kind: "condition", valueType: "boolean" },
    ],
  },
};

/**
 * 获取节点类型对应的端口定义。
 *
 * @param nodeOrType 节点实例或节点类型。传入实例时目前仍按节点类型返回静态端口定义。
 * @returns 该节点类型的输入、输出端口定义。
 *
 * 设计上端口是节点类型的静态契约，不写入节点实例；因此这里是编辑器和运行器读取端口规则的统一入口。
 */
export function getPropNodePorts(nodeOrType: PropNode | PropNodeType): PropNodePortDefinition {
  const type = typeof nodeOrType === "string" ? nodeOrType : nodeOrType.type;
  const ports = PROP_NODE_PORTS[type];
  if (!isActionPropNodeType(type)) return ports;
  return {
    inputs: ports.inputs.map((port) => (
      port.id === "targets" ? { ...port, multiple: true } : port
    )),
    outputs: ports.outputs,
  };
}

function isActionPropNodeType(type: PropNodeType) {
  return type === "actionGrow" ||
    type === "actionExtraYield" ||
    type === "actionPurify" ||
    type === "actionProtect" ||
    type === "actionAccelerate" ||
    type === "actionTransform" ||
    type === "actionSpawn" ||
    type === "actionEliminate" ||
    type === "actionCollect" ||
    type === "actionHarvest" ||
    type === "actionRarityWeight" ||
    type === "actionExtendCommission" ||
    type === "actionSwapPosition" ||
    type === "actionPermanentBoost" ||
    type === "actionTriggerVisual";
}

/**
 * 创建带随机后缀的图元素 id。
 *
 * @param prefix id 前缀，用于区分节点、连线、变量等元素来源。
 * @returns 由前缀、当前时间和随机片段组成的字符串 id。
 *
 * 时间片段便于大致判断创建顺序；随机片段用于降低同一毫秒内批量创建时的冲突概率。
 */
export function createPropGraphId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * 把节点数组转换成运行器友好的节点字典。
 *
 * @param nodes 节点数组。
 * @returns 以节点 id 为 key 的节点字典。
 *
 * 如果数组中存在重复 id，后出现的节点会覆盖先出现的节点；调用方应在写入前保证 id 唯一。
 */
export function createPropNodeMap(nodes: PropNode[]): PropNodeMap {
  return nodes.reduce<PropNodeMap>((map, node) => {
    map[node.id] = node;
    return map;
  }, {});
}

/**
 * 创建一个默认运行节点。
 *
 * @param type 要创建的节点类型。未传入时创建回合开始事件节点。
 * @returns 带唯一 id、固定类型和默认参数的运行节点。
 *
 * 该函数只创建运行数据，不写入画布坐标、标题或备注。编辑器状态需要由
 * `createDefaultNodeEditorState` 另行生成。
 */
export function createDefaultPropNode(
  type: PropNodeType = "eventTurnStart",
): PropNode {
  const id = createPropGraphId("node");

  switch (type) {
    case "eventTurnStart":
      return { id, type: "eventTurnStart", data: { interval: 0 } };
    case "eventAcquire":
      return { id, type: "eventAcquire", data: {} };
    case "eventUse":
      return { id, type: "eventUse", data: {} };
    case "eventEliminated":
      return { id, type: "eventEliminated", data: {} };
    case "targetSelf":
      return { id, type: "targetSelf", data: {} };
    case "targetBoard":
      return { id, type: "targetBoard", data: { scope: "all" } };
    case "prototypeLibrary":
      return { id, type: "prototypeLibrary", data: { propType: 1 } };
    case "prototypeSelect":
      return { id, type: "prototypeSelect", data: {} };
    case "targetFilter":
      return { id, type: "targetFilter", data: {} };
    case "filterRarity":
      return { id, type: "filterRarity", data: {} };
    case "filterTag":
      return { id, type: "filterTag", data: {} };
    case "filterPrototype":
      return { id, type: "filterPrototype", data: {} };
    case "filterRandom":
      return { id, type: "filterRandom", data: {} };
    case "actionGrow":
      return { id, type: "actionGrow", data: { negative: false } };
    case "actionExtraYield":
      return { id, type: "actionExtraYield", data: { negative: false } };
    case "actionPurify":
      return { id, type: "actionPurify", data: { negative: false } };
    case "actionProtect":
      return { id, type: "actionProtect", data: { negative: false } };
    case "actionAccelerate":
      return { id, type: "actionAccelerate", data: { negative: false } };
    case "actionTransform":
      return { id, type: "actionTransform", data: { negative: false } };
    case "actionSpawn":
      return { id, type: "actionSpawn", data: { negative: false } };
    case "actionEliminate":
      return { id, type: "actionEliminate", data: { negative: false } };
    case "actionCollect":
      return { id, type: "actionCollect", data: { negative: false } };
    case "actionHarvest":
      return { id, type: "actionHarvest", data: { negative: false } };
    case "actionRarityWeight":
      return { id, type: "actionRarityWeight", data: { negative: false } };
    case "actionExtendCommission":
      return { id, type: "actionExtendCommission", data: { negative: false } };
    case "actionSwapPosition":
      return { id, type: "actionSwapPosition", data: { negative: false } };
    case "actionPermanentBoost":
      return { id, type: "actionPermanentBoost", data: { negative: false, mutexTag: "default" } };
    case "actionTriggerVisual":
      return { id, type: "actionTriggerVisual", data: { negative: false } };
    case "intConstant":
      return { id, type: "intConstant", data: { value: 1 } };
    case "intAttribute":
      return { id, type: "intAttribute", data: { attribute: "rarity" } };
    case "intQuantity":
      return { id, type: "intQuantity", data: {} };
    case "intKindCount":
      return { id, type: "intKindCount", data: {} };
    case "intRandom":
      return { id, type: "intRandom", data: {} };
    case "intOperation":
      return { id, type: "intOperation", data: { op: "add" } };
    case "floatConstant":
      return { id, type: "floatConstant", data: { value: 1 } };
    case "floatAttribute":
      return { id, type: "floatAttribute", data: { attribute: "rarity" } };
    case "floatRandom":
      return { id, type: "floatRandom", data: {} };
    case "floatOperation":
      return { id, type: "floatOperation", data: { op: "add" } };
    case "roundNumber":
      return { id, type: "roundNumber", data: { mode: "floor" } };
    case "rarityConstant":
      return { id, type: "rarityConstant", data: { value: 1 } };
    case "tagConstant":
      return { id, type: "tagConstant", data: { value: 1 } };
    case "logicCompareInt":
      return { id, type: "logicCompareInt", data: { op: "gte" } };
    case "logicCompareFloat":
      return { id, type: "logicCompareFloat", data: { op: "gte" } };
    case "logicContains":
      return { id, type: "logicContains", data: {} };
    case "logicProbability":
      return { id, type: "logicProbability", data: { probability: 0.5 } };
  }
}

/**
 * 为运行节点创建默认编辑器状态。
 *
 * @param node 运行节点。
 * @param index 节点在批量创建时的顺序，用于生成默认标题编号和初始横向位置。
 * @returns 包含节点 id、默认标题和默认画布位置的编辑器状态。
 */
export function createDefaultNodeEditorState(
  node: PropNode,
  index = 0,
): PropGraphNodeEditorState {
  return {
    nodeId: node.id,
    title: getDefaultNodeTitle(node.type, index),
    position: {
      x: index * 300,
      y: 0,
    },
  };
}

/**
 * 创建一份默认道具图。
 *
 * @returns 不包含数据库 `_id` 的默认道具配置。
 *
 * 默认图包含一个回合开始事件、一个盘面目标、一个整数常量和一个成长动作，
 * 用于让新建道具立即具备可编辑的最小有效逻辑结构。
 */
export function createDefaultPropGraph(): Omit<PropGraph, "_id"> {
  const eventNode = createDefaultPropNode("eventTurnStart");
  const targetNode = createDefaultPropNode("targetBoard");
  const amountNode = createDefaultPropNode("intConstant") as IntConstantNode;
  amountNode.data = { value: 1 };
  const actionNode = createDefaultPropNode("actionGrow");

  return {
    name: "",
    assetId: "Empty_001",
    type: 1,
    rarity: 1,
    tags: [],
    yield: 0,
    schemaVersion: 3,
    graph: {
      entryNodeIds: [eventNode.id],
      nodes: createPropNodeMap([eventNode, targetNode, amountNode, actionNode]),
      flows: [
        {
          id: createPropGraphId("flow"),
          from: { nodeId: eventNode.id, portId: "flow" },
          to: { nodeId: actionNode.id, portId: "flow" },
          order: 0,
        },
      ],
      bindings: [
        {
          id: createPropGraphId("binding"),
          kind: "target",
          source: { nodeId: targetNode.id, portId: "targets" },
          target: { nodeId: actionNode.id, portId: "targets" },
        },
        {
          id: createPropGraphId("binding"),
          kind: "value",
          source: { nodeId: amountNode.id, portId: "value" },
          target: { nodeId: actionNode.id, portId: "amount" },
        },
      ],
    },
    editor: {
      nodes: [
        createDefaultNodeEditorState(eventNode, 0),
        createDefaultNodeEditorState(targetNode, 1),
        createDefaultNodeEditorState(amountNode, 2),
        createDefaultNodeEditorState(actionNode, 3),
      ],
      comments: [],
    },
  };
}

/**
 * 根据节点类型生成默认标题。
 *
 * @param type 节点类型。
 * @param index 节点顺序，从 0 开始。
 * @returns 带中文节点名称和 1 基编号的默认标题。
 *
 * 标题只用于编辑器展示，不参与运行器逻辑；运行器应始终依据 `type` 和 `data` 判断节点行为。
 */
function getDefaultNodeTitle(type: PropNodeType, index: number) {
  const number = index + 1;
  switch (type) {
    case "eventTurnStart":
      return `回合开始 ${number}`;
    case "eventAcquire":
      return `持有触发 ${number}`;
    case "eventUse":
      return `主动使用 ${number}`;
    case "eventEliminated":
      return `消除触发 ${number}`;
    case "targetSelf":
      return `自身 ${number}`;
    case "targetBoard":
      return `盘面原料 ${number}`;
    case "prototypeLibrary":
      return `原型库 ${number}`;
    case "prototypeSelect":
      return `原型 ${number}`;
    case "targetFilter":
      return `筛选 ${number}`;
    case "filterRarity":
      return `\u54c1\u8d28\u7b5b\u9009 ${number}`;
    case "filterTag":
      return `\u6807\u7b7e\u7b5b\u9009 ${number}`;
    case "filterPrototype":
      return `原型筛选 ${number}`;
    case "filterRandom":
      return `随机筛选 ${number}`;
    case "actionGrow":
      return `成长 ${number}`;
    case "actionExtraYield":
      return `额外产出 ${number}`;
    case "actionPurify":
      return `净化 ${number}`;
    case "actionProtect":
      return `保护 ${number}`;
    case "actionAccelerate":
      return `加速 ${number}`;
    case "actionTransform":
      return `转化 ${number}`;
    case "actionSpawn":
      return `生成 ${number}`;
    case "actionEliminate":
      return `消除 ${number}`;
    case "actionCollect":
      return `收集 ${number}`;
    case "actionHarvest":
      return `收获 ${number}`;
    case "actionRarityWeight":
      return `品质权重 ${number}`;
    case "actionExtendCommission":
      return `延长调和 ${number}`;
    case "actionSwapPosition":
      return `调换位置 ${number}`;
    case "actionPermanentBoost":
      return `永久加成 ${number}`;
    case "actionTriggerVisual":
      return `触发表现 ${number}`;
    case "intConstant":
      return `整数常量 ${number}`;
    case "intAttribute":
      return `整数属性 ${number}`;
    case "intQuantity":
      return `数量 ${number}`;
    case "intKindCount":
      return `种类 ${number}`;
    case "intRandom":
      return `整数随机 ${number}`;
    case "intOperation":
      return `整数运算 ${number}`;
    case "floatConstant":
      return `浮点常量 ${number}`;
    case "floatAttribute":
      return `浮点属性 ${number}`;
    case "floatRandom":
      return `浮点随机 ${number}`;
    case "floatOperation":
      return `浮点运算 ${number}`;
    case "roundNumber":
      return `取整 ${number}`;
    case "rarityConstant":
      return `\u54c1\u8d28\u5e38\u91cf ${number}`;
    case "tagConstant":
      return `\u6807\u7b7e\u5e38\u91cf ${number}`;
    case "logicCompareInt":
      return `整数比较 ${number}`;
    case "logicCompareFloat":
      return `浮点比较 ${number}`;
    case "logicContains":
      return `包含 ${number}`;
    case "logicProbability":
      return `概率 ${number}`;
  }
}
